import React from "react";
import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  IconButton,
  SimpleGrid,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { BsArrowDown, BsArrowUp, BsChevronDown, BsChevronUp, BsFiles, BsGear, BsTrash } from "react-icons/bs";
import KraItemsTable from "./KraItemsTable";
import RatingCriteriaItemsTable from "./RatingCriteriaItemsTable";
import SectionValidationMessage from "./SectionValidationMessage";
import {
  getSectionStatus,
  getSectionValidation,
  isStaticSource,
  sourceLabels,
  statusColorSchemes,
  statusLabels,
  toUiSource,
} from "./templateSectionUtils";

const TemplateSectionPanel = ({
  control,
  register,
  setValue,
  section,
  sectionIndex,
  itemWeightageMode,
  isDirty,
  onEditSettings,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onDelete,
}) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const sectionHeaderBg = useColorModeValue("blue.700", "blue.900");
  const sectionHeaderColor = "white";
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const totalBg = useColorModeValue("gray.50", "gray.900");
  const uiSource = toUiSource(section);
  const status = getSectionStatus(section, isDirty);
  const validation = getSectionValidation(section, itemWeightageMode);
  const isKra = section.code === "KRA" || section.type === "GOALS";

  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" overflow="hidden" minW={0}>
      <Box bg={sectionHeaderBg} color={sectionHeaderColor} px={4} py={3}>
        <Flex justify="space-between" align={{ base: "stretch", lg: "center" }} direction={{ base: "column", lg: "row" }} gap={3}>
          <Box>
            <HStack wrap="wrap">
              <Text fontWeight="bold" letterSpacing="0" wordBreak="break-word">
                SECTION {sectionIndex + 1}: {(section.title || "Untitled section").toUpperCase()}
              </Text>
              <Badge colorScheme="whiteAlpha">{section.weightage || 0}%</Badge>
              <Badge colorScheme={statusColorSchemes[status] || "gray"}>{statusLabels[status] || status}</Badge>
            </HStack>
            <Text mt={1} fontSize="sm" opacity={0.9} overflowWrap="anywhere">
              {section.description || "No section purpose added."}
            </Text>
          </Box>
          <HStack spacing={1} wrap="wrap" justify={{ base: "flex-start", lg: "flex-end" }}>
            <IconButton size="sm" variant="ghost" color="white" icon={<BsGear />} aria-label="Edit section settings" onClick={onEditSettings} />
            <IconButton size="sm" variant="ghost" color="white" icon={<BsFiles />} aria-label="Duplicate section" onClick={onDuplicate} />
            <IconButton size="sm" variant="ghost" color="white" icon={<BsArrowUp />} aria-label="Move section up" onClick={onMoveUp} />
            <IconButton size="sm" variant="ghost" color="white" icon={<BsArrowDown />} aria-label="Move section down" onClick={onMoveDown} />
            <IconButton size="sm" variant="ghost" color="white" icon={collapsed ? <BsChevronDown /> : <BsChevronUp />} aria-label="Collapse section" onClick={() => setCollapsed((value) => !value)} />
            <IconButton size="sm" variant="ghost" color="white" icon={<BsTrash />} aria-label="Delete section" onClick={onDelete} />
          </HStack>
        </Flex>
      </Box>

      <Box px={4} py={3} borderBottomWidth="1px" borderColor={borderColor}>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
          <Text fontSize="sm"><b>Code:</b> {section.code || "-"}</Text>
          <Text fontSize="sm"><b>Type:</b> {section.type || "-"}</Text>
          <Text fontSize="sm"><b>Source:</b> {sourceLabels[uiSource] || uiSource}</Text>
          <Text fontSize="sm"><b>Calculation:</b> {section.calculationMethod || "-"}</Text>
        </SimpleGrid>
      </Box>

      <Collapse in={!collapsed} animateOpacity>
        <Box p={4}>
          {isStaticSource(section) ? (
            isKra ? (
              <KraItemsTable control={control} sectionIndex={sectionIndex} register={register} setValue={setValue} />
            ) : (
              <RatingCriteriaItemsTable control={control} sectionIndex={sectionIndex} register={register} sectionTitle={section.title} setValue={setValue} />
            )
          ) : (
            <Box bg={totalBg} borderRadius="md" p={3}>
              <Text fontSize="sm">
                {uiSource === "EMPLOYEE_GOALS"
                  ? "This section is populated from approved employee goals when reviews are generated."
                  : uiSource === "COMPETENCY_MASTER"
                    ? "This section is populated from mapped competency master records."
                    : uiSource === "APPRAISAL_360"
                      ? "This section is populated from eligible 360 appraisal campaigns."
                      : "No external source is required for this manager-entered section."}
              </Text>
            </Box>
          )}

          <SectionValidationMessage messages={validation.messages} />

          <Flex
            mt={4}
            justify="space-between"
            align={{ base: "stretch", md: "center" }}
            direction={{ base: "column", md: "row" }}
            bg={totalBg}
            borderRadius="md"
            p={3}
            wrap="wrap"
            gap={2}
          >
            <Text fontSize="sm">Section weightage: <b>{section.weightage || 0}</b></Text>
            <Text fontSize="sm">Rows: <b>{(section.items || []).filter((item) => item.active !== false).length}</b></Text>
            <Text fontSize="sm">Item allocation: <b>{itemWeightageMode === "EQUAL_DISTRIBUTION" ? "Equal distribution" : validation.itemTotal}</b></Text>
            {validation.messages.length ? (
              <Button size="xs" variant="outline" onClick={onEditSettings} width={{ base: "100%", md: "auto" }}>Fix settings</Button>
            ) : null}
          </Flex>
        </Box>
      </Collapse>
    </Box>
  );
};

export default TemplateSectionPanel;
